import { Inject, Injectable } from '@angular/core';
import { HttpClient, HttpErrorResponse, HttpHeaders } from '@angular/common/http';
import { Observable, of, throwError } from 'rxjs';
import { switchMap, timeout } from 'rxjs/operators';
import { UNIFIED_PATIENTS_CONFIG, type UnifiedPatientsConfig } from './unified-patients-config.token';

export interface CreateLinkedPatientRequest {
	iteroPatientId?: string;
	invisalignPatientId?: string;
	patient: {
		firstName: string;
		lastName: string;
		dateOfBirth: string;
		gender: string;
		zipCode: string;
		chartNumber: string;
	};
}

export interface CreateLinkedPatientResponse {
	Success: boolean;
	Reason?: string;
	StatusCode?: number;
	Data?: {
		IteroPatientId: string;
		InvisalignPatientId: string;
		IteroPatientCreated: boolean;
		IdsPatientCreated: boolean;
		LinkageCreated: boolean;
		Source: string;
	};
}

export interface SaveIteroPatientResponse {
	patients: SavedPatientDto[];
	conflictedPatients: SavedPatientDto[];
}

export interface SavedPatientDto {
	uid: string;
	id?: number;
	firstName: string;
	lastName: string;
	chartNumber: string;
	DateOfBirth?: string;
	gender?: number;
}

export interface PatientFields {
	id?: string | null;
	firstName: string;
	lastName: string;
	chartNumber: string;
	gender: string;
	dateOfBirth: string;
}

export interface SearchPatientsResponse {
	results: SearchPatientItem[];
	hasMore: boolean;
	pageSize: number;
	offset: number;
	total: number;
}

export interface SearchPatientItem {
	iteroPatientId: string | null;
	invisalignPatientId: string | null;
	firstName: string;
	lastName: string;
	dateOfBirth: string | null;
	gender: number | null;
	chartNumber: string;
	type: string;
	dateUpdated: string | null;
}

export interface ThumbnailResponse {
	invisalignPatientId: string;
	imageRole: string;
	thumbnails: { size: string; url: string }[];
	status: 'found' | 'notFound' | 'unavailable';
}

@Injectable({ providedIn: 'root' })
export class PatientLinkApiService {
	private static readonly REQUEST_TIMEOUT_MS = 10000;

	clinicianId: string | null = null;

	constructor(
		private readonly http: HttpClient,
		@Inject(UNIFIED_PATIENTS_CONFIG) private readonly config: UnifiedPatientsConfig
	) {}

	createLinkedPatient(request: CreateLinkedPatientRequest): Observable<CreateLinkedPatientResponse> {
		return this.http
			.post<CreateLinkedPatientResponse>(`${this.config.getBffUrl()}/api/v1/patient-link/create-linked-patient`, request, {
				params: { companyId: String(this.config.getCompanyId()) },
				headers: this.buildHeaders()
			})
			.pipe(
				timeout(PatientLinkApiService.REQUEST_TIMEOUT_MS),
				switchMap(response => {
					if (!response.Success) {
						return throwError(
							() =>
								new HttpErrorResponse({
									error: response,
									status: response.StatusCode ?? 400,
									statusText: response.Reason ?? 'create_linked_patient_failed'
								})
						);
					}

					return of(response);
				})
			);
	}

	createPatient(patient: PatientFields, dryRun = false): Observable<SaveIteroPatientResponse> {
		const apiUrl = this.buildPatientApiUrl('save');

		return this.http
			.post<SaveIteroPatientResponse>(apiUrl, this.buildPatientBody(patient), {
				params: { companyId: String(this.config.getCompanyId()), dryRun: String(dryRun) },
				headers: this.buildHeaders()
			})
			.pipe(timeout(PatientLinkApiService.REQUEST_TIMEOUT_MS));
	}

	updatePatient(patient: PatientFields, uid: string, dryRun = false): Observable<SaveIteroPatientResponse> {
		const apiUrl = this.buildPatientApiUrl('update');

		return this.http
			.patch<SaveIteroPatientResponse>(apiUrl, this.buildPatientBody(patient), {
				params: { companyId: String(this.config.getCompanyId()), UID: uid, dryRun: String(dryRun) },
				headers: this.buildHeaders()
			})
			.pipe(timeout(PatientLinkApiService.REQUEST_TIMEOUT_MS));
	}

	searchPatients(firstName: string, lastName: string, sources = 'ITero,Invisalign'): Observable<SearchPatientsResponse> {
		return this.http
			.get<SearchPatientsResponse>(`${this.config.getBffUrl()}/api/v1/patient-link/search`, {
				params: {
					companyId: String(this.config.getCompanyId()),
					clinicianId: this.clinicianId ?? '',
					sources,
					firstName,
					lastName
				},
				headers: this.buildHeaders()
			})
			.pipe(timeout(PatientLinkApiService.REQUEST_TIMEOUT_MS));
	}

	getPatientById(patientId: string): Observable<SearchPatientsResponse> {
		return this.http
			.get<SearchPatientsResponse>(`${this.config.getBffUrl()}/api/v1/patient-link/search`, {
				params: {
					companyId: String(this.config.getCompanyId()),
					clinicianId: this.clinicianId ?? '',
					sources: 'ITero,Invisalign',
					patientId
				},
				headers: this.buildHeaders()
			})
			.pipe(timeout(PatientLinkApiService.REQUEST_TIMEOUT_MS));
	}

	getRecentPatients(): Observable<SearchPatientsResponse> {
		const companyId = this.config.getCompanyId();

		if (!companyId) {
			return of({ results: [], hasMore: false, pageSize: 0, offset: 0, total: 0 });
		}

		const today = new Date().toISOString().split('T')[0];

		return this.http
			.get<SearchPatientsResponse>(`${this.config.getBffUrl()}/api/v1/patient-link/search`, {
				params: {
					clinicianId: this.clinicianId ?? '',
					sources: 'ITero,Invisalign',
					order: 'desc',
					companyId: String(companyId),
					updatedDateFrom: today
				},
				headers: this.buildHeaders()
			})
			.pipe(timeout(PatientLinkApiService.REQUEST_TIMEOUT_MS));
	}

	getThumbnails(patientIds: string[]): Observable<ThumbnailResponse[]> {
		return this.http
			.post<ThumbnailResponse[]>(
				`${this.config.getBffUrl()}/api/v1/patient-link/thumbnails`,
				{ patientIds },
				{
					params: { companyId: String(this.config.getCompanyId()) },
					headers: this.buildHeaders()
				}
			)
			.pipe(timeout(PatientLinkApiService.REQUEST_TIMEOUT_MS));
	}

	private buildHeaders(): HttpHeaders {
		const contactId = this.config.getContactId();

		return contactId ? new HttpHeaders({ 'X-Doctor-ID': String(contactId) }) : new HttpHeaders();
	}

	private buildPatientApiUrl(action: string): string {
		const rawUrl = this.config.getHostRxApiUrl();
		const host = new URL(rawUrl).origin;

		return rawUrl.includes('RxEditor') ? `${host}/patients/${action}` : `${host}/api/v1/patients/${action}`;
	}

	private buildPatientBody(patient: PatientFields) {
		return {
			...(patient.id ? { uid: patient.id } : {}),
			firstName: patient.firstName,
			lastName: patient.lastName,
			chartNumber: patient.chartNumber,
			gender: patient.gender === 'Male' ? 0 : patient.gender === 'Female' ? 1 : null,
			dateOfBirth: patient.dateOfBirth || null
		};
	}
}
